var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useCreateAssistantStream } from 'app/domains/assistant/hooks/useCreateAssistantStream';
import { ChatMessageType } from 'shared/domains/apollo/generated/types';
import { isApolloError } from 'shared/domains/apollo/isApolloError';
import { RunWorkflowCheckDocument } from './RunWorkflowCheck.gql';
import { SetupWorkflowCheckDocument } from './SetupWorkflowCheck.gql';
import { SyncAssistantCreditsDocument } from './SyncAssistantCredits.gql';
import { WorkflowApprovalResultSchema, WorkflowSignatoryResultSchema, } from './schemas';
import { useWorkflowCheckState } from '../../useWorkflowCheckState';
import { WorkflowCheckErrorSubcodes } from './types';
export const useStreamWorkflowCheck = (documentId, type) => {
    const { documentState, resetDocumentState, setDocumentState } = useWorkflowCheckState(documentId);
    const workflowResult = documentState[type].result;
    const createEventSource = useCreateAssistantStream();
    const [setupWorkflowMutation] = useMutation(SetupWorkflowCheckDocument);
    const [runWorkflowMutation] = useMutation(RunWorkflowCheckDocument);
    const [syncCreditsQuery] = useLazyQuery(SyncAssistantCreditsDocument);
    const [approvalResult, setApprovalResult] = useState(documentState.approval.result || {
        summary: '',
        isApprovalRequired: false,
        approvers: [],
    });
    const [signatoryResult, setSignatoryResult] = useState(documentState.signatory.result || {
        summary: '',
        signatories: [],
    });
    const [state, setState] = useState(workflowResult ? 'done' : 'initial');
    const [error, setError] = useState(null);
    const handleStreamResponse = (_a) => __awaiter(void 0, [_a], void 0, function* ({ chatId, streamToken }) {
        const evtSource = yield createEventSource(streamToken, chatId, 'document');
        const closeStream = () => {
            evtSource.close();
            setState('done');
        };
        evtSource.onmessage = (event) => {
            const [, [kind, payload]] = JSON.parse(event.data);
            if (kind === 'END') {
                closeStream();
                return;
            }
            if (kind === 'ERROR') {
                setError('streaming');
                closeStream();
                return;
            }
            if (type === 'approval') {
                const { success, data } = WorkflowApprovalResultSchema.safeParse(JSON.parse(payload));
                if (success) {
                    setApprovalResult(data);
                    setDocumentState('approval', { result: data });
                }
                else {
                    setError('streaming');
                    closeStream();
                }
            }
            else {
                const { success, data } = WorkflowSignatoryResultSchema.safeParse(JSON.parse(payload));
                if (success) {
                    setSignatoryResult(data);
                    setDocumentState('signatory', { result: data });
                }
                else {
                    setError('streaming');
                    closeStream();
                }
            }
        };
        // The server will close the connection after sending the end token, this is treated as
        // an error state by the EventSource, and we thus close the connection.
        evtSource.onerror = () => {
            closeStream();
        };
    });
    const resetWorkflowCheck = () => {
        setError(null);
        setState('initial');
        resetDocumentState(type);
        if (type === 'approval') {
            setApprovalResult({ summary: '', isApprovalRequired: false, approvers: [] });
        }
        else {
            setSignatoryResult({ summary: '', signatories: [] });
        }
    };
    const setupWorkflow = (context) => __awaiter(void 0, void 0, void 0, function* () {
        const { data } = yield setupWorkflowMutation({ variables: { documentId } });
        const chatId = data === null || data === void 0 ? void 0 : data.createDocumentChat.id;
        if (!chatId) {
            throw new Error('Failed to create chat');
        }
        return { chatId, context };
    });
    const handleError = (err) => {
        var _a, _b;
        setState('initial');
        if (isApolloError(err)) {
            const subcode = (_b = (_a = err.graphQLErrors[0]) === null || _a === void 0 ? void 0 : _a.extensions) === null || _b === void 0 ? void 0 : _b.subcode;
            if (subcode === WorkflowCheckErrorSubcodes.InsufficientCredits) {
                syncCreditsQuery();
                setError('credits');
            }
        }
        else {
            setError('setup');
        }
    };
    const runWorkflow = (_b) => __awaiter(void 0, [_b], void 0, function* ({ chatId, workflowId, context, }) {
        var _c;
        const runWorkflowType = type === 'approval' ? ChatMessageType.WorkflowApprovalCheck : ChatMessageType.WorkflowSignatoriesSuggest;
        const { data } = yield runWorkflowMutation({ variables: { chatId, workflowId, context, type: runWorkflowType } });
        const streamToken = (_c = data === null || data === void 0 ? void 0 : data.lastChat.messages[0]) === null || _c === void 0 ? void 0 : _c.streamToken;
        if (!streamToken) {
            throw new Error('Failed to send workflow');
        }
        return { chatId, streamToken };
    });
    const startWorkflowCheck = (workflowId, context) => __awaiter(void 0, void 0, void 0, function* () {
        setState('evaluating');
        setDocumentState(type, { latestWorkflowId: workflowId });
        yield setupWorkflow(context)
            .then(({ chatId, context: ctx }) => runWorkflow({ chatId, workflowId, context: ctx }))
            .then(handleStreamResponse)
            .catch(handleError);
    });
    const rerunWorkflowCheck = (context) => {
        const { latestWorkflowId } = documentState[type];
        if (latestWorkflowId) {
            startWorkflowCheck(latestWorkflowId, context);
        }
    };
    return { startWorkflowCheck, resetWorkflowCheck, rerunWorkflowCheck, state, error, approvalResult, signatoryResult };
};
